import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { Apps, Edit } from '@mui/icons-material';
import LoadingSpinner from '../../components/LoadingSpinner';
import { TintedIcon } from '../../users/details/TintedIcon';
import { SubscriptionApp, SubscriptionModel } from '../../interfaces';
import { useSubscriptionApps } from '../SubscriptionAppsDialog';

export function AppsPanel({ subscription, onChange }: { subscription: SubscriptionModel; onChange(): void }) {
  const { apps, loading, error } = useSubscriptionApps();
  const appIds = subscription.appIds ?? [];
  const included = (apps ?? []).filter((x) => appIds.includes(x.appId));
  // An app the apps service no longer lists is still in the subscription, so it is shown rather than hidden.
  const unknown = apps ? appIds.filter((id) => !apps.some((x) => x.appId === id)) : [];

  const byCategory = new Map<string, SubscriptionApp[]>();
  for (const app of included) {
    const category = app.category || 'Andre';
    byCategory.set(category, [...(byCategory.get(category) ?? []), app]);
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Typography variant="body2" color="text.secondary">
          Alle som har abonnementet får disse selvstendige appene. Endringer gjelder med en gang. Apper i anvisninger følger tilgangen til
          anvisningen.
        </Typography>
        <Button variant="outlined" size="small" startIcon={<Edit />} onClick={onChange} sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
          Endre apper
        </Button>
      </Stack>

      <Box sx={{ mt: 3 }}>
        {loading && <LoadingSpinner />}
        {error && <Alert severity="error">Fikk ikke hentet appene fra apps-tjenesten. Abonnementet har {appIds.length} apper.</Alert>}
        {apps && appIds.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Abonnementet gir ingen apper.
          </Typography>
        )}
        <Stack spacing={2.5}>
          {[...byCategory.entries()].map(([category, inCategory]) => (
            <Stack key={category} spacing={1}>
              <Typography variant="overline" color="text.secondary">
                {category}
              </Typography>
              {inCategory.map((app) => (
                <AppRow key={app.appId} name={app.name} />
              ))}
            </Stack>
          ))}
          {unknown.length > 0 && (
            <Stack spacing={1}>
              <Typography variant="overline" color="text.secondary">
                Ikke selvstendig app, eller ukjent for apps-tjenesten
              </Typography>
              {unknown.map((id) => (
                <AppRow key={id} name={id} />
              ))}
            </Stack>
          )}
        </Stack>
      </Box>
    </Box>
  );
}

function AppRow({ name }: { name: string }) {
  return (
    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
      <TintedIcon>
        <Apps fontSize="small" />
      </TintedIcon>
      <Typography variant="body2">{name}</Typography>
    </Stack>
  );
}
